// components/AboutUs.js
import React, { useState, useEffect, useRef } from 'react';
import './AboutUs.css';

/* -------------------------------------------------
   Count-up hook: animates 0 → end when in view
   ------------------------------------------------- */
const useCountUp = (end, duration = 2000, decimals = 0) => {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  // Start animation only when the element enters the viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Animate with requestAnimationFrame (ease-out cubic)
  useEffect(() => {
    if (!started) return;

    let frame;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out
      setValue(parseFloat((end * eased).toFixed(decimals)));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [started, end, duration, decimals]);

  return { ref, value };
};

/* -------------------------------------------------
   Stat Card component
   ------------------------------------------------- */
const StatCard = ({ stat }) => {
  const decimals = stat.decimals || 0;
  const { ref, value } = useCountUp(stat.end, 2000, decimals);

  return (
    <div ref={ref} className="about-stat-card">
      <div className="about-stat-value">
        {value.toFixed(decimals)}
        {stat.suffix && <span>{stat.suffix}</span>}
        {stat.star && <span className="about-stat-star">★</span>}
      </div>
      <div className="about-stat-label">{stat.label}</div>
    </div>
  );
};

/* -------------------------------------------------
   AboutUs component
   ------------------------------------------------- */
const AboutUs = () => {
  const stats = [
    { id: 1, end: 6, suffix: 'K+', label: 'Happy Customers' },
    { id: 2, end: 480, suffix: '+', label: 'Verified Partners' },
    { id: 3, end: 2, suffix: 'K+', label: 'Bookings Completed' },
    { id: 4, end: 4.9, decimals: 1, label: 'User Rating', star: true },
  ];

  const services = [
    {
      id: 1,
      title: 'Premium Car Wash',
      description: 'Professional cleaning at the best prices',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 13L4 8H20L19 13M5 13L3 18H7M5 13H19M19 13L21 18H17M7 18H17M7 18C7 19.1046 6.10457 20 5 20C3.89543 20 3 19.1046 3 18C3 16.8954 3.89543 16 5 16C6.10457 16 7 16.8954 7 18ZM17 18C17 19.1046 16.1046 20 15 20C13.8954 20 13 19.1046 13 18C13 16.8954 13.8954 16 15 16C16.1046 16 17 16.8954 17 18Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="9" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="15" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      ),
    },
    {
      id: 2,
      title: 'Turf Booking',
      description: 'Cricket & Football turfs available',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L15 8H22L16 12L18 18L12 14L6 18L8 12L2 8H9L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      ),
    },
    {
      id: 3,
      title: 'Tournament Management',
      description: 'Real-time updates & registration',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      ),
    },
  ];

  return (
    <section className="about-us-section" id="about">
      <div className="about-us-container">
        {/* Section Header */}
        <div className="about-header">
          <span className="about-tag">ABOUT US</span>
          <h2 className="about-title">
            About <span className="about-title-highlight">QSlot</span>
          </h2>
          <p className="about-subtitle">
            Your all-in-one booking platform for Car Wash, Turf, and Tournaments
          </p>
        </div>

        {/* Who We Are */}
        <div className="about-content">
          <h3 className="about-content-title">Who We Are?</h3>
          <p className="about-content-text">
            Welcome to <span className="about-highlight">QSLOT</span> — your
            ultimate all-in-one booking platform for Car Wash, Turf, and
            Tournament Management. We're a passionate team dedicated to creating
            seamless digital experiences that simplify your daily needs.
          </p>
        </div>

        {/* Vision Section */}
        <div className="about-vision">
          <p className="about-vision-text">
            QSLOT was born from the vision to revolutionize how people book
            services. Whether you need a premium car wash, want to book a turf
            for your sports activities, or are looking to organize tournaments —
            we've got you covered. Our platform brings together quality service
            providers and customers on a single, easy-to-use platform.
          </p>

          <p className="about-vision-text">
            With QSLOT, you can book professional car wash services at the best
            prices, reserve premium turfs for cricket and football, and manage
            tournaments with real-time updates — all from your smartphone. We've
            partnered with verified service providers to ensure you get the best
            quality every time.
          </p>

          <p className="about-vision-text about-mission-text">
            Our mission is to make service booking hassle-free, affordable, and
            accessible to everyone. With thousands of happy customers and
            hundreds of verified partners, QSLOT is your trusted companion for
            all your booking needs.
          </p>
        </div>

        {/* Stats Section */}
        <div className="about-stats-grid">
          {stats.map((stat) => (
            <StatCard key={stat.id} stat={stat} />
          ))}
        </div>

        {/* Services Section */}
        <div className="about-services-grid">
          {services.map((service) => (
            <div key={service.id} className="about-service-card">
              <div className="about-service-icon">{service.icon}</div>
              <h4 className="about-service-title">{service.title}</h4>
              <p className="about-service-description">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutUs;